import icn_bar_chart_horizontal from 'assets/images/icons/icn_bar_chart_horizontal.png';

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import FunnelProgressBar from 'components/FunnelProgressBar';

import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import Api from "pages/AInsight/ServiceExperience/util/api";

const DEFAULT_DISPLAY_COUNT = 2;

const SuccessRateDrawer = ({ chartData, auth }) => {
  const theme = useTheme();
  const { sendBatch } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const [loading, setLoading] = useState(true);
  const [showAllAp, setShowAllAp] = useState(false);
  const [showAllSsid, setShowAllSsid] = useState(false);
  const [apData, setApData] = useState([]);
  const [ssidData, setSsidData] = useState([]);
  const [requestUuids] = useState({
    devices: uuidv4(),
    ssids: uuidv4(),
  });

  const fetchData = async () => {
    if (!auth) return;
    setLoading(true);
    const api = Api.init(auth);
    const devicesParams = api.getConnectSuccessRateDevicesParams();
    const ssidsParams = api.getConnectSuccessRateSsidsParams();
    const { getSendBatchData } = await sendBatch([
      { ...devicesParams, requestUuid: requestUuids.devices },
      { ...ssidsParams, requestUuid: requestUuids.ssids },
    ]);
    const devicesRes = getSendBatchData({ requestUuid: requestUuids.devices })?.data;
    const ssidsRes = getSendBatchData({ requestUuid: requestUuids.ssids })?.data;
    if (devicesRes?.data) {
      const sortedDevices = [...devicesRes.data].sort((a, b) => Number(a.successRatePercent || 0) - Number(b.successRatePercent || 0));
      setApData(sortedDevices);
    }
    if (ssidsRes?.data) {
      const sortedSsids = [...ssidsRes.data].sort((a, b) => Number(a.successRatePercent || 0) - Number(b.successRatePercent || 0));
      setSsidData(sortedSsids);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth]);

  const apTableData = useMemo(() =>
    showAllAp ? apData : apData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAllAp, apData]);

  const ssidTableData = useMemo(() =>
    showAllSsid ? ssidData : ssidData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAllSsid, ssidData]);

  const apColumns = useMemo(() => [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("attempts", {
      header: "Attempts",
      cell: (info) => info.getValue().toLocaleString(),
    }),
    columnHelper.accessor("successRatePercent", {
      header: "Success Rate",
      cell: (info) => `${info.getValue()}%`,
    }),
  ], [columnHelper]);

  const ssidColumns = useMemo(() => [
    columnHelper.accessor("ssidName", {
      header: "SSID Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("successRatePercent", {
      header: "Success Rate",
      cell: (info) => `${info.getValue()}%`,
    }),
  ], [columnHelper]);

  const apTable = useTableInstance({
    tableName: "topApSuccessRateDrawerTable",
    columns: apColumns,
    data: apTableData,
    enableSorting: false,
    enablePagination: false,
  });

  const ssidTable = useTableInstance({
    tableName: "topSsidSuccessRateDrawerTable",
    columns: ssidColumns,
    data: ssidTableData,
    enableSorting: false,
    enablePagination: false,
  });

  if (!chartData) return null;

  const { attempts = 0, success = 0 } = chartData;
  const showMoreAp = apData.length > DEFAULT_DISPLAY_COUNT;
  const showMoreSsid = ssidData.length > DEFAULT_DISPLAY_COUNT;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_bar_chart_horizontal} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          Captive Portal Connection Success Rate
        </Typography>
      </Box>
      <Box>
        <FunnelProgressBar attempts={attempts} success={success} />
      </Box>
      <CollapseCard title="Top AP by Lowest Success Rate (Top 5)">
        <Box sx={{ width: "100%" }}>
          <TableField {...apTable} loading={loading} />
          {showMoreAp && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                onClick={() => setShowAllAp(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0,
                  padding: 0,
                  color: theme.palette.primary.greySecound,
                  cursor: "pointer",
                }}
              >
                {showAllAp ? "" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>
      <CollapseCard title="Top SSID by Lowest Success Rate">
        <Box sx={{ width: "100%" }}>
          <TableField {...ssidTable} loading={loading} />
          {showMoreSsid && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                onClick={() => setShowAllSsid(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0,
                  padding: 0,
                  color: theme.palette.primary.greySecound,
                  cursor: "pointer",
                }}
              >
                {showAllSsid ? "" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default SuccessRateDrawer;
